import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const DEFAULT_STATUS_ORDER = ['backlog', 'to_do', 'in_progress', 'review', 'testing', 'done'];

const initialState = {
  board: {
    tasks: {},
    columns: {},
    ordered: [],
  },
  boardStatus: {
    loading: false,
    empty: false,
    error: null,
  },
  tasks: {
    byStatus: {},
    byId: {},
    loading: false,
    error: null,
  },
  filters: {
    search: '',
    priority: null,
    type: null,
    assignee: null,
    tags: [],
  },
  // Subtasks state - keyed by taskId
  subtasks: {
    byTaskId: {}, // { taskId: { subtasks: [], loading: false, error: null, assignees: [] } }
  },
};

const slice = createSlice({
  name: 'kanban',
  initialState,
  reducers: {
    // GET BOARD
    getBoardStart(state) {
      state.boardStatus.loading = true;
      state.boardStatus.empty = false;
      state.boardStatus.error = null;
    },
    getBoardFailure(state, action) {
      state.boardStatus.loading = false;
      state.boardStatus.empty = false;
      state.boardStatus.error = action.payload;
    },
    getBoardSuccess(state, action) {
      const board = action.payload;
      
      // API returns tasks as an object keyed by task ID (taskMap)
      // Example: { "1": {task...}, "2": {task...} }
      const tasksMap = board.tasks || {};
      
      // API returns columns as an object with column IDs as keys
      // Each column has: { id, name, taskIds: [...] }
      const columnsObj = board.columns || {};
      
      // API returns ordered as columnOrder array
      const ordered = board.columnOrder || board.ordered || [];
      
      const allTasks = {};
      const columns = {};
      
      // Transform columns to match component format
      Object.keys(columnsObj).forEach((columnId) => {
        const column = columnsObj[columnId];
        columns[columnId] = {
          id: column.id || columnId,
          name: column.name || columnId,
          taskIds: Array.isArray(column.taskIds) 
            ? column.taskIds.map((id) => String(id))
            : [],
        };
      });
      
      // Transform tasks to match component format
      Object.keys(tasksMap).forEach((taskId) => {
        const task = tasksMap[taskId];
        if (!task?.id) {
          console.warn('Task without ID found:', task);
          return;
        }
        
        // Handle assignees - API may return assignees array or assignee object
        let assignees = [];
        
        
        if (task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0) {
          assignees = task.assignees.map((a) => {
            // Get avatar from profile if available
            const avatarPath = a.profile?.avatarPath || a.avatarPath || '';
            // Construct full avatar URL if path exists
            const avatarUrl = avatarPath 
              ? (avatarPath.startsWith('http') ? avatarPath : avatarPath) 
              : '';
            
            return {
              id: a.id || a,
              name: a.name || '',
              email: a.email || '',
              avatarUrl: avatarUrl || a.avatar || a.avatarUrl || '',
            };
          });
          
          // Debug: Log processed assignees for task 3
          if (String(task.id) === '3') {
          }
        } else if (task.assignee) {
          // Single assignee object (fallback for old format)
          const assigneeObj = typeof task.assignee === 'object' ? task.assignee : { id: task.assignee };
          const avatarPath = assigneeObj.profile?.avatarPath || assigneeObj.avatarPath || '';
          const avatarUrl = avatarPath 
            ? (avatarPath.startsWith('http') ? avatarPath : avatarPath) 
            : '';
          
          assignees = [{
            id: assigneeObj.id || task.assignee,
            name: assigneeObj.name || '',
            email: assigneeObj.email || '',
            avatarUrl: avatarUrl || assigneeObj.avatar || assigneeObj.avatarUrl || '',
          }];
        } else if (task.assignedTo) {
          // Just an ID (minimal fallback)
          assignees = [{ id: task.assignedTo, name: '', email: '', avatarUrl: '' }];
        }

        // Handle dates - startDate and dueDate
        let dueDateArray = [null, null];
        if (task.startDate && task.dueDate) {
          dueDateArray = [task.startDate, task.dueDate];
        } else if (task.dueDate) {
          dueDateArray = [task.dueDate, task.dueDate];
        } else if (task.startDate) {
          dueDateArray = [task.startDate, null];
        }

        // Transform task to component format
        allTasks[String(task.id)] = {
          id: String(task.id),
          name: task.title || task.name || '',
          description: task.description || '',
          status: task.status || 'backlog',
          priority: task.priority || 'medium',
          attachments: task.attachments || [],
          labels: task.labels || task.tags || [],
          comments: task.comments || [],
          commentCount: task.commentCount ?? 0,
          assignee: assignees,
          createdBy: task.createdBy,
          due: dueDateArray,
          reporter: task.creator
            ? {
                id: task.creator.id,
                name: task.creator.name || '',
                email: task.creator.email || '',
                avatarUrl: task.creator.profile?.avatarPath 
                  ? (task.creator.profile.avatarPath.startsWith('http') 
                      ? task.creator.profile.avatarPath 
                      : `${process.env.HOST_API || ''}${task.creator.profile.avatarPath}`)
                  : (task.creator.avatar || task.creator.avatarUrl || ''),
              }
            : {
                id: task.createdBy || 0,
                name: '',
                email: '',
                avatarUrl: '',
              },
        };
      });
      
      // Ensure all columns have taskIds array (even if empty)
      Object.keys(columns).forEach((columnId) => {
        if (!Array.isArray(columns[columnId].taskIds)) {
          columns[columnId].taskIds = [];
        }
      });
      
      state.boardStatus.loading = false;
      state.boardStatus.empty = !ordered?.length;
      state.boardStatus.error = null;

      state.board = {
        tasks: allTasks,
        columns,
        ordered: ordered,
      };
    },

    // CREATE NEW COLUMN
    createColumnSuccess(state, action) {
      const column = action.payload;

      state.board.columns = {
        ...state.board.columns,
        [column.id]: column,
      };

      state.board.ordered.push(column.id);
    },

    setColumns(state, action) {
      state.board.columns = action.payload;
    },

    setOrdered(state, action) {
      state.board.ordered = action.payload;
    },

    addTask(state, action) {
      const { task, columnId } = action.payload;

      if (task?.id && columnId && state.board.columns[columnId]) {
        state.board.tasks[task.id] = task;
        if (!state.board.columns[columnId].taskIds) {
          state.board.columns[columnId].taskIds = [];
        }
        state.board.columns[columnId].taskIds.push(task.id);
      }
    },

    deleteTask(state, action) {
      const { taskId, columnId } = action.payload;

      if (columnId && state.board.columns[columnId]?.taskIds) {
        state.board.columns[columnId].taskIds = state.board.columns[columnId].taskIds.filter(
          (id) => id !== taskId
        );
      }

      if (taskId) {
        state.board.tasks = omit(state.board.tasks, [taskId]);
      }
    },

    // UPDATE COLUMN
    updateColumnSuccess(state, action) {
      const column = action.payload;

      state.board.columns[column.id] = column;
    },

    // DELETE COLUMN
    deleteColumnSuccess(state, action) {
      const columnId = action.payload;
      const deletedColumn = state.board.columns?.[columnId];

      if (deletedColumn) {
        state.board.columns = omit(state.board.columns, [columnId]);
        if (deletedColumn.taskIds && Array.isArray(deletedColumn.taskIds)) {
          state.board.tasks = omit(state.board.tasks, [...deletedColumn.taskIds]);
        }
        state.board.ordered = (state.board.ordered || []).filter((id) => id !== columnId);
      }
    },

    // TASK MANAGEMENT - NEW API
    // GET TASKS
    getTasksStart(state) {
      state.tasks.loading = true;
      state.tasks.error = null;
    },
    getTasksSuccess(state, action) {
      const tasks = action.payload || [];
      
      // Group tasks by status
      const tasksByStatus = groupBy(tasks, 'status');
      
      // Create byId map
      const tasksById = keyBy(tasks, 'id');
      
      // Get all unique statuses from tasks
      const allStatuses = [...new Set(tasks.map((t) => t.status).filter(Boolean))];

      // Sort tasks by position within each status
      state.tasks.byStatus = {};
      allStatuses.forEach((status) => {
        const statusTasks = tasksByStatus[status] || [];
        state.tasks.byStatus[status] = statusTasks.sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) {
            return a.position - b.position;
          }
          return (a.order || 0) - (b.order || 0);
        });
      });

      state.tasks.byId = tasksById;
      state.tasks.loading = false;
      state.tasks.error = null;

      // Also update the old board structure for compatibility
      const columns = {};
      const ordered = [];
      const boardTasks = {};

      const statusOrderToUse = allStatuses.length > 0 ? allStatuses : DEFAULT_STATUS_ORDER;
      statusOrderToUse.forEach((status) => {
        const statusLabel = status;
        
        const columnId = status;
        ordered.push(columnId);
        
        columns[columnId] = {
          id: columnId,
          name: statusLabel,
          taskIds: (state.tasks.byStatus[status] || []).map((t) => String(t.id)),
        };
        
        // Add tasks to boardTasks in the format expected by minimal.cc
        (state.tasks.byStatus[status] || []).forEach((task) => {
          // Handle assignees - API may return assignees array or assignee object
          // The backend's getTasks endpoint should include assignees, but if it doesn't,
          // we handle the single assignee case as well
          let assignees = [];
          
          // Check if task has assignees array (new format with multiple assignees)
          if (task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0) {
            assignees = task.assignees.map((a) => ({
              id: a.id || a,
              name: a.name || '',
              email: a.email || '',
              avatarUrl: a.avatar || a.avatarUrl || '',
            }));
          } else if (task.assignee) {
            // Single assignee object (fallback for old format)
            const assigneeObj = typeof task.assignee === 'object' ? task.assignee : { id: task.assignee };
            assignees = [{
              id: assigneeObj.id || task.assignee,
              name: assigneeObj.name || '',
              email: assigneeObj.email || '',
              avatarUrl: assigneeObj.avatar || assigneeObj.avatarUrl || '',
            }];
          } else if (task.assignedTo) {
            // Just an ID (minimal fallback)
            assignees = [{ id: task.assignedTo, name: '', email: '', avatarUrl: '' }];
          }

          boardTasks[String(task.id)] = {
            id: String(task.id),
            name: task.title || '',
            description: task.description || '',
            status: task.status || 'backlog',
            priority: task.priority || 'medium',
            attachments: task.attachments || [],
            labels: task.tags || [],
            comments: task.comments || [],
            commentCount: task.commentCount ?? 0,
            assignee: assignees,
            createdBy: task.createdBy,
            due: task.dueDate ? [new Date(task.dueDate), null] : [null, null],
            reporter: task.creator
              ? {
                  id: task.creator.id,
                  name: task.creator.name || '',
                  avatarUrl: task.creator.avatar || '',
                }
              : {
                  id: task.createdBy || 0,
                  name: '',
                  avatarUrl: '',
                },
          };
        });
      });
      
      state.board = {
        tasks: boardTasks,
        columns,
        ordered,
      };
    },
    getTasksFailure(state, action) {
      state.tasks.loading = false;
      state.tasks.error = action.payload;
    },

    // CREATE TASK
    createTaskSuccess(state, action) {
      const task = action.payload;
      const status = task.status || 'backlog';
      
      state.tasks.byId[task.id] = task;
      
      // Add to appropriate status array
      if (!state.tasks.byStatus[status]) {
        state.tasks.byStatus[status] = [];
      }
      state.tasks.byStatus[status].push(task);
      
      // Sort by position
      state.tasks.byStatus[status].sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) {
          return a.position - b.position;
        }
        return (a.order || 0) - (b.order || 0);
      });
      
      // Update old board structure for minimal.cc compatibility
      if (state.board.columns[status]) {
        if (!state.board.columns[status].taskIds.includes(String(task.id))) {
          state.board.columns[status].taskIds.push(String(task.id));
        }
      }
      
      // Handle assignees
      let assignees = [];
      if (task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0) {
        assignees = task.assignees.map((a) => ({
          id: a.id || a,
          name: a.name || '',
          email: a.email || '',
          avatarUrl: a.avatar || a.avatarUrl || '',
        }));
      } else if (task.assignee) {
        const assigneeObj = typeof task.assignee === 'object' ? task.assignee : { id: task.assignee };
        assignees = [{
          id: assigneeObj.id || task.assignee,
          name: assigneeObj.name || '',
          email: assigneeObj.email || '',
          avatarUrl: assigneeObj.avatar || assigneeObj.avatarUrl || '',
        }];
      } else if (task.assignedTo) {
        assignees = [{ id: task.assignedTo, name: '', email: '', avatarUrl: '' }];
      }
      
      state.board.tasks[String(task.id)] = {
        id: String(task.id),
        name: task.title || task.name || '',
        description: task.description || '',
        status: task.status || 'backlog',
        priority: task.priority || 'medium',
        attachments: task.attachments || [],
        labels: task.tags || [],
        comments: task.comments || [],
        commentCount: task.commentCount ?? 0,
        assignee: assignees,
        createdBy: task.createdBy,
        due: task.dueDate ? [new Date(task.dueDate), null] : [null, null],
        reporter: task.creator
          ? {
              id: task.creator.id,
              name: task.creator.name || '',
              avatarUrl: task.creator.avatar || task.creator.avatarUrl || '',
            }
          : {
              id: task.createdBy || 0,
              name: '',
              avatarUrl: '',
            },
      };
    },

    // UPDATE TASK
    updateTaskSuccess(state, action) {
      const task = action.payload;
      const oldTask = state.tasks.byId[task.id];
      const oldStatus = oldTask?.status;
      const newStatus = task.status;
      
      // Update task in byId
      state.tasks.byId[task.id] = task;
      
      // If status changed, move task between arrays
      if (oldStatus && oldStatus !== newStatus) {
        // Remove from old status
        if (state.tasks.byStatus[oldStatus]) {
          state.tasks.byStatus[oldStatus] = state.tasks.byStatus[oldStatus].filter(
            (t) => t.id !== task.id
          );
        }
        
        // Update old board structure
        if (state.board.columns[oldStatus]) {
          state.board.columns[oldStatus].taskIds = state.board.columns[oldStatus].taskIds.filter(
            (id) => id !== String(task.id)
          );
        }
        
        // Add to new status
        if (!state.tasks.byStatus[newStatus]) {
          state.tasks.byStatus[newStatus] = [];
        }
        state.tasks.byStatus[newStatus].push(task);
        
        // Update new board structure
        if (state.board.columns[newStatus]) {
          if (!state.board.columns[newStatus].taskIds.includes(String(task.id))) {
            state.board.columns[newStatus].taskIds.push(String(task.id));
          }
        }
        
        // Sort by position
        state.tasks.byStatus[newStatus].sort((a, b) => {
          if (a.position !== undefined && b.position !== undefined) {
            return a.position - b.position;
          }
          return (a.order || 0) - (b.order || 0);
        });
      } else {
        // Update in place
        const statusArray = state.tasks.byStatus[newStatus] || [];
        const index = statusArray.findIndex((t) => t.id === task.id);
        if (index !== -1) {
          statusArray[index] = task;
          statusArray.sort((a, b) => {
            if (a.position !== undefined && b.position !== undefined) {
              return a.position - b.position;
            }
            return (a.order || 0) - (b.order || 0);
          });
        }
      }
      
      // Update board.tasks with assignees
      if (state.board.tasks[String(task.id)]) {
        // Handle assignees - prioritize assignees array if present
        let assignees = [];
        if (task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0) {
          assignees = task.assignees.map((a) => ({
            id: a.id || a,
            name: a.name || '',
            email: a.email || '',
            avatarUrl: a.avatar || a.avatarUrl || '',
          }));
        } else if (task.assignee) {
          // Single assignee object (fallback)
          const assigneeObj = typeof task.assignee === 'object' ? task.assignee : { id: task.assignee };
          assignees = [{
            id: assigneeObj.id || task.assignee,
            name: assigneeObj.name || '',
            email: assigneeObj.email || '',
            avatarUrl: assigneeObj.avatar || assigneeObj.avatarUrl || '',
          }];
        } else if (task.assignedTo) {
          assignees = [{ id: task.assignedTo, name: '', email: '', avatarUrl: '' }];
        }
        
        state.board.tasks[String(task.id)] = {
          ...state.board.tasks[String(task.id)],
          name: task.title || state.board.tasks[String(task.id)].name,
          description: task.description !== undefined ? task.description : state.board.tasks[String(task.id)].description,
          priority: task.priority || state.board.tasks[String(task.id)].priority,
          // Always update assignees if we have them, otherwise preserve existing
          assignee: assignees.length > 0 ? assignees : (state.board.tasks[String(task.id)].assignee || []),
          labels: task.tags || state.board.tasks[String(task.id)].labels,
          due: task.dueDate ? [new Date(task.dueDate), null] : state.board.tasks[String(task.id)].due,
          commentCount: task.commentCount !== undefined ? task.commentCount : state.board.tasks[String(task.id)].commentCount,
        };
      }
    },

    // MOVE TASK (drag & drop)
    moveTaskSuccess(state, action) {
      const { task, oldStatus, newStatus, newPosition } = action.payload;
      
      // Update task in byId
      state.tasks.byId[task.id] = task;
      
      // Remove from old status
      if (oldStatus && state.tasks.byStatus[oldStatus]) {
        state.tasks.byStatus[oldStatus] = state.tasks.byStatus[oldStatus].filter(
          (t) => t.id !== task.id
        );
      }
      
      // Add to new status at correct position
      if (!state.tasks.byStatus[newStatus]) {
        state.tasks.byStatus[newStatus] = [];
      }
      
      const newStatusArray = state.tasks.byStatus[newStatus];
      newStatusArray.splice(newPosition, 0, task);
      
      // Re-sort by position
      newStatusArray.sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) {
          return a.position - b.position;
        }
        return (a.order || 0) - (b.order || 0);
      });
    },

    // DELETE TASK
    deleteTaskSuccess(state, action) {
      const { taskId, status } = action.payload;
      
      // Remove from byId
      state.tasks.byId = omit(state.tasks.byId, [taskId]);
      
      // Remove from status array
      if (status && state.tasks.byStatus[status]) {
        state.tasks.byStatus[status] = state.tasks.byStatus[status].filter(
          (t) => t.id !== taskId
        );
      }
      
      // Update old board structure for minimal.cc compatibility
      if (status && state.board.columns[status]) {
        state.board.columns[status].taskIds = state.board.columns[status].taskIds.filter(
          (id) => id !== String(taskId)
        );
      }
      state.board.tasks = omit(state.board.tasks, [String(taskId)]);
    },

    // SET FILTERS
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    // CLEAR FILTERS
    clearFilters(state) {
      state.filters = {
        search: '',
        priority: null,
        type: null,
        assignee: null,
        tags: [],
      };
    },

    // SUBTASKS
    getSubtasksStart(state, action) {
      const { taskId } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      state.subtasks.byTaskId[taskId].loading = true;
      state.subtasks.byTaskId[taskId].error = null;
    },
    getSubtasksSuccess(state, action) {
      const { taskId, subtasks } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      state.subtasks.byTaskId[taskId].subtasks = subtasks;
      state.subtasks.byTaskId[taskId].loading = false;
    },
    getSubtasksFailure(state, action) {
      const { taskId, error } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      state.subtasks.byTaskId[taskId].loading = false;
      state.subtasks.byTaskId[taskId].error = error;
    },
    getSubtaskAssigneesSuccess(state, action) {
      const { taskId, assignees } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      state.subtasks.byTaskId[taskId].assignees = assignees;
    },
    createSubtaskSuccess(state, action) {
      const { taskId, subtask } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      state.subtasks.byTaskId[taskId].subtasks.push(subtask);
      // Sort by order
      state.subtasks.byTaskId[taskId].subtasks.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
    },
    updateSubtaskSuccess(state, action) {
      const { taskId, subtask } = action.payload;
      if (!state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId] = { subtasks: [], loading: false, error: null, assignees: [] };
      }
      const index = state.subtasks.byTaskId[taskId].subtasks.findIndex((s) => s.id === subtask.id);
      if (index !== -1) {
        state.subtasks.byTaskId[taskId].subtasks[index] = subtask;
      }
    },
    deleteSubtaskSuccess(state, action) {
      const { taskId, subtaskId } = action.payload;
      if (state.subtasks.byTaskId[taskId]) {
        state.subtasks.byTaskId[taskId].subtasks = state.subtasks.byTaskId[taskId].subtasks.filter(
          (s) => s.id !== subtaskId
        );
      }
    },
    // Update commentCount for a task
    updateTaskCommentCount(state, action) {
      const { taskId, commentCount } = action.payload;
      const taskIdStr = String(taskId);
      if (state.board.tasks[taskIdStr]) {
        state.board.tasks[taskIdStr].commentCount = commentCount;
      }
    },
  },
});

// Reducer
export default slice.reducer;

export const { 
  setOrdered, 
  setColumns, 
  addTask, 
  deleteTask,
  setFilters,
  clearFilters,
  getSubtasksStart,
  getSubtasksSuccess,
  getSubtasksFailure,
  getSubtaskAssigneesSuccess,
  createSubtaskSuccess,
  updateSubtaskSuccess,
  deleteSubtaskSuccess,
  updateTaskCommentCount,
} = slice.actions;

// ----------------------------------------------------------------------

export function getBoard(kanbanId) {
  return async (dispatch) => {
    dispatch(slice.actions.getBoardStart());
    try {
      const response = await axios.get(API_ENDPOINTS.projects.kanban(kanbanId));
      
      // API returns { success: true, board: {...} }
      if (response.data.success && response.data.board) {
        dispatch(slice.actions.getBoardSuccess(response.data.board));
      } else {
        dispatch(slice.actions.getBoardFailure('Invalid response format'));
      }
    } catch (error) {
      console.error('Error fetching kanban board:', error);
      dispatch(slice.actions.getBoardFailure(error.message || 'Failed to fetch kanban board'));
    }
  };
}

// ----------------------------------------------------------------------

export function createColumn(kanbanId, newData) {
  return async (dispatch) => {
    try {
      const data = newData;
      const response = await axios.post(API_ENDPOINTS.projects.kanban(kanbanId), data, {
        params: {
          endpoint: 'create',
        },
      });
      dispatch(slice.actions.createColumnSuccess(response.data.column));
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function updateColumn(kanbanId, columnId, newData) {
  return async (dispatch) => {
    try {
      const data = {
        columnId,
        newData,
      };
      const response = await axios.post(API_ENDPOINTS.projects.kanban(kanbanId), data, {
        params: {
          endpoint: 'update',
        },
      });
      dispatch(slice.actions.updateColumnSuccess(response.data.column));
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function deleteColumn(kanbanId, columnId) {
  return async (dispatch) => {
    try {
      const data = {
        columnId,
      };
      const response = await axios.post(API_ENDPOINTS.projects.kanban(kanbanId), data, {
        params: {
          endpoint: 'delete',
        },
      });
      dispatch(slice.actions.deleteColumnSuccess(response.data.columnId));
    } catch (error) {
      console.error(error);
    }
  };
}

// ----------------------------------------------------------------------

export function updateTaskPosition(taskId, status, position, order = 0) {
  return async (dispatch) => {
    try {
      // API endpoint: PUT /api/projects/tasks/:taskId/position
      const response = await axios.put(API_ENDPOINTS.projects.kabanEdit(taskId), {
        status,
        position,
        order,
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating task position:', error);
      throw error;
    }
  };
}

// ----------------------------------------------------------------------
// NEW TASK MANAGEMENT API FUNCTIONS

export function getTasks(projectId) {
  return async (dispatch) => {
    dispatch(slice.actions.getTasksStart());
    try {
      const response = await axios.get(API_ENDPOINTS.tasks.list(projectId));

      // console.log(response.data.data);
      
      if (response.data.success) {
        dispatch(slice.actions.getTasksSuccess(response.data.data || []));
      } else {
        dispatch(slice.actions.getTasksFailure('Invalid response format'));
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      dispatch(slice.actions.getTasksFailure(error.message || 'Failed to fetch tasks'));
      throw error;
    }
  };
}

export function createTask(taskData) {
  return async (dispatch) => {
    try {
      const response = await axios.post(API_ENDPOINTS.tasks.create, taskData);
      
      if (response.data.success) {
        dispatch(slice.actions.createTaskSuccess(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };
}

export function updateTask(taskId, taskData) {
  return async (dispatch) => {
    try {
      // Prepare update data - use assignees array if provided
      const updateData = { ...taskData };
      
      // If assignees array is provided, use it; otherwise keep assignedTo for backward compatibility
      if (updateData.assignees && Array.isArray(updateData.assignees)) {
        // assignees array is already set, use it
        // Also set assignedTo to first assignee for backward compatibility
        if (updateData.assignees.length > 0) {
          updateData.assignedTo = updateData.assignees[0];
        } else {
          updateData.assignedTo = null;
        }
      } else if (updateData.assignedTo) {
        // If only assignedTo is provided, convert to assignees array
        updateData.assignees = [updateData.assignedTo];
      }
      
      const response = await axios.put(API_ENDPOINTS.tasks.update(taskId), updateData);
      
      if (response.data.success) {
        dispatch(slice.actions.updateTaskSuccess(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      // Check if it's a 403 error (not creator)
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can edit this task');
      }
      throw error;
    }
  };
}

export function updateTaskAssignees(taskId, assignees) {
  return async (dispatch, getState) => {
    try {
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      // Convert assignees to array of IDs
      const assigneeIds = Array.isArray(assignees)
        ? assignees.map(a => (typeof a === 'object' ? a.id : a)).filter(Boolean)
        : [];

      const response = await axios.put(API_ENDPOINTS.tasks.update(normalizedTaskId), {
        assignees: assigneeIds,
      });

      if (response.data.success) {
        const responseData = response.data.data;
        if (responseData) {
          dispatch(slice.actions.updateTaskSuccess(responseData));
        }
        return responseData;
      } else {
        throw new Error(response.data.message || 'Failed to update task assignees');
      }
    } catch (error) {
      console.error('Error updating task assignees:', error);
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can edit this task');
      }
      throw error;
    }
  };
}

export function moveTask(taskId, status, position) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const task = state.kanban.tasks.byId[taskId];
      const oldStatus = task?.status;
      
      // Optimistically update
      if (task) {
        const updatedTask = { ...task, status, position };
        dispatch(slice.actions.moveTaskSuccess({
          task: updatedTask,
          oldStatus,
          newStatus: status,
          newPosition: position,
        }));
      }
      
      // Call API
      const response = await axios.patch(API_ENDPOINTS.tasks.move(taskId), {
        status,
        position,
      });
      
      if (response.data.success) {
        // Update with server response
        dispatch(slice.actions.updateTaskSuccess(response.data.data));
        return response.data.data;
      } else {
        // Rollback on error
        if (task && oldStatus) {
          dispatch(getTasks(task.projectId));
        }
        throw new Error(response.data.message || 'Failed to move task');
      }
    } catch (error) {
      console.error('Error moving task:', error);
      // Rollback on error
      const state = getState();
      const task = state.kanban.tasks.byId[taskId];
      if (task?.projectId) {
        dispatch(getTasks(task.projectId));
      }
      throw error;
    }
  };
}

export function deleteTaskById(taskId) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const task = state.kanban.tasks.byId[taskId] || state.kanban.board.tasks[String(taskId)];
      const status = task?.status;
      
      // Optimistically remove
      dispatch(slice.actions.deleteTaskSuccess({ taskId, status }));
      
      // Call API
      const response = await axios.delete(API_ENDPOINTS.tasks.delete(taskId));
      
      if (!response.data.success) {
        // Rollback on error
        const projectId = task?.projectId || state.kanban.tasks.byId[taskId]?.projectId;
        if (projectId) {
          dispatch(getTasks(projectId));
        }
        throw new Error(response.data.message || 'Failed to delete task');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error deleting task:', error);
      // Rollback on error
      const state = getState();
      const task = state.kanban.tasks.byId[taskId] || state.kanban.board.tasks[String(taskId)];
      const projectId = task?.projectId || state.kanban.tasks.byId[taskId]?.projectId;
      if (projectId) {
        dispatch(getTasks(projectId));
      }
      throw error;
    }
  };
}

// ----------------------------------------------------------------------
// SUBTASKS

export function getSubtasks(taskId) {
  return async (dispatch) => {
    try {
      // Normalize taskId to ensure consistent state key
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      dispatch(slice.actions.getSubtasksStart({ taskId: normalizedTaskId }));
      const response = await axios.get(API_ENDPOINTS.tasks.subtasks.list(normalizedTaskId));
      
      if (response.data.success) {
        dispatch(slice.actions.getSubtasksSuccess({ taskId: normalizedTaskId, subtasks: response.data.data || [] }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch subtasks');
      }
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      dispatch(slice.actions.getSubtasksFailure({ taskId: normalizedTaskId, error: error.message || 'Failed to fetch subtasks' }));
      throw error;
    }
  };
}

export function getSubtaskAssignees(taskId) {
  return async (dispatch) => {
    try {
      // Normalize taskId to ensure consistent state key
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      const response = await axios.get(API_ENDPOINTS.tasks.subtasks.assignees(normalizedTaskId));
      
      if (response.data.success) {
        dispatch(slice.actions.getSubtaskAssigneesSuccess({ taskId: normalizedTaskId, assignees: response.data.data || [] }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch subtask assignees');
      }
    } catch (error) {
      console.error('Error fetching subtask assignees:', error);
      throw error;
    }
  };
}

export function getSubtaskEligibleAssignees(taskId, subtaskId) {
  return async () => {
    try {
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      const normalizedSubtaskId = typeof subtaskId === 'string' ? parseInt(subtaskId, 10) : subtaskId;
      
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      if (!normalizedSubtaskId || isNaN(normalizedSubtaskId)) {
        throw new Error('Invalid subtask ID');
      }
      
      const response = await axios.get(API_ENDPOINTS.tasks.subtasks.eligibleAssignees(normalizedTaskId, normalizedSubtaskId));
      
      if (response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data.message || 'Failed to fetch eligible assignees');
      }
    } catch (error) {
      console.error('Error fetching eligible assignees:', error);
      throw error;
    }
  };
}

export function addSubtaskAssignees(taskId, subtaskId, assignees) {
  return async (dispatch) => {
    try {
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      const normalizedSubtaskId = typeof subtaskId === 'string' ? parseInt(subtaskId, 10) : subtaskId;
      
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      if (!normalizedSubtaskId || isNaN(normalizedSubtaskId)) {
        throw new Error('Invalid subtask ID');
      }
      
      // Convert assignees to array of IDs
      const assigneeIds = Array.isArray(assignees)
        ? assignees.map(a => (typeof a === 'object' ? a.id : a)).filter(Boolean)
        : [];

      const response = await axios.post(
        API_ENDPOINTS.tasks.subtasks.addAssignees(normalizedTaskId, normalizedSubtaskId),
        { assignees: assigneeIds }
      );

      if (response.data.success) {
        // Refetch subtasks to get updated data
        await dispatch(getSubtasks(normalizedTaskId));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to add subtask assignees');
      }
    } catch (error) {
      console.error('Error adding subtask assignees:', error);
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can edit subtasks');
      }
      throw error;
    }
  };
}

export function createSubtask(taskId, subtaskData) {
  return async (dispatch) => {
    try {
      // Normalize taskId to ensure consistent state key
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      const response = await axios.post(API_ENDPOINTS.tasks.subtasks.create(normalizedTaskId), subtaskData);
      
      if (response.data.success) {
        dispatch(slice.actions.createSubtaskSuccess({ taskId: normalizedTaskId, subtask: response.data.data }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create subtask');
      }
    } catch (error) {
      console.error('Error creating subtask:', error);
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can create subtasks');
      }
      throw error;
    }
  };
}

export function updateSubtask(taskId, subtaskId, subtaskData) {
  return async (dispatch) => {
    try {
      // Normalize taskId to ensure consistent state key
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      const response = await axios.put(API_ENDPOINTS.tasks.subtasks.update(normalizedTaskId, subtaskId), subtaskData);
      
      if (response.data.success) {
        dispatch(slice.actions.updateSubtaskSuccess({ taskId: normalizedTaskId, subtask: response.data.data }));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update subtask');
      }
    } catch (error) {
      console.error('Error updating subtask:', error);
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can edit subtasks');
      }
      throw error;
    }
  };
}

export function deleteSubtask(taskId, subtaskId) {
  return async (dispatch) => {
    try {
      // Normalize taskId to ensure consistent state key
      const normalizedTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
      if (!normalizedTaskId || isNaN(normalizedTaskId)) {
        throw new Error('Invalid task ID');
      }
      
      const response = await axios.delete(API_ENDPOINTS.tasks.subtasks.delete(normalizedTaskId, subtaskId));
      
      if (response.data.success) {
        dispatch(slice.actions.deleteSubtaskSuccess({ taskId: normalizedTaskId, subtaskId }));
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete subtask');
      }
    } catch (error) {
      console.error('Error deleting subtask:', error);
      if (error.response?.status === 403) {
        throw new Error('Only the task creator can delete subtasks');
      }
      throw error;
    }
  };
}